// ────────────────────────────────────────────────────────────────────────
// The fan's stored sport (P0, site templates §6.0 / D14)
//
// The restore and persist used to live inside the SportSwitcher presentation
// component. SportProvider owns them now (`setActiveSport(id, { persist })`,
// `restoreStoredSport()`, built on the helpers below), so the switcher's markup (or a template that
// renders it differently, or not at all) can't change which sport loads.
//
// PURE MOVE: the restore still fires exactly when it did before. That was an
// effect in every mounted SportSwitcher (deps: the tenant's sport ids and
// default sport), declared before the switcher's early returns, so it ran:
//   - whenever a <Navigation/> mounted (each one mounts two switchers,
//     desktop + mobile), on every route that renders one, INCLUDING the
//     detail routes where the switcher itself is hidden and on single-sport
//     tenants (it bails only when the tenant has no sports at all);
//   - again whenever the tenant's sport ids or default sport changed while
//     one was mounted.
// `useStoredSportRestore()` (./useStoredSportRestore) reproduces that trigger; the chrome calls it
// where the switcher's effect used to sit. Changing *when* the restore
// fires (e.g. #416: deep links to an off-sport entity) is a separate fix.
// ────────────────────────────────────────────────────────────────────────

export interface StoredSportSnapshot {
  /** The tenant's sport ids (`tenantSportIds(league)`). */
  sports: string[]
  /** `league.default_sport_id`. */
  defaultSportId: string | null | undefined
  /** The active sport the caller rendered with. */
  activeSportId: string
}

/** localStorage key holding the fan's pick for this host. */
export function storedSportKey(): string {
  return `mcl_active_sport_${window.location.hostname}`
}

export function readStoredSport(): string | null {
  try { return localStorage.getItem(storedSportKey()) } catch { return null }
}

export function writeStoredSport(id: string): void {
  try {
    localStorage.setItem(storedSportKey(), id)
  } catch {
    // localStorage can fail in private mode — switch still works for this session.
  }
}

/**
 * The tenant's public sports: distinct sport ids across its seasons plus its
 * default sport, sorted so basketball/soccer appear in a stable order
 * regardless of season creation order.
 *
 * #568: when the payload carries `enabled_sport_ids`, a disabled or archived
 * sport is left out (it keeps its seasons, but it is not public). The switcher,
 * the stored-sport restore, the off-sport auto-switch and the deep-link gate
 * all read this list, so such a sport is never offered, restored or switched
 * to. The default sport always stays, so the list is never empty.
 */
export function tenantSportIds(
  league:
    | { seasons?: Array<{ sport_id: string }> | null; default_sport_id?: string | null; enabled_sport_ids?: string[] | null }
    | null
    | undefined,
): string[] {
  const set = new Set<string>()
  for (const s of league?.seasons ?? []) set.add(s.sport_id)
  const enabled = league?.enabled_sport_ids
  if (Array.isArray(enabled)) {
    for (const id of Array.from(set)) if (!enabled.includes(id)) set.delete(id)
  }
  if (league?.default_sport_id) set.add(league.default_sport_id)
  return Array.from(set).sort()
}

/**
 * Which sport the restore should switch to, or null for no change. The
 * stored pick wins when it's still a sport on this tenant; otherwise the
 * tenant's default (or its first sport).
 */
export function resolveStoredSport(snapshot: StoredSportSnapshot, stored: string | null): string | null {
  const { sports, defaultSportId, activeSportId } = snapshot
  if (sports.length === 0) return null
  if (stored && sports.includes(stored)) {
    return stored !== activeSportId ? stored : null
  }
  const fallback = defaultSportId || sports[0]
  return fallback && fallback !== activeSportId ? fallback : null
}
